function normalizeString(str) {
  return (str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
}

import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import { unformatCNPJ, formatCNPJ, isValidCNPJ } from '../../utils/cnpjUtils';
import { unformatTelefone, formatTelefone } from '../../utils/telefoneUtils';
import { unformatCelular, formatCelular } from '../../utils/celularUtils';
import { unformatCEP, formatCEP } from '../../utils/cepUtils';
import CNPJMaskInput from '../masks/CNPJMaskInput';
import TelefoneMaskInput from '../masks/TelefoneMaskInput';
import CelularMaskInput from '../masks/CelularMaskInput';
import CEPMaskInput from '../masks/CEPMaskInput';
import useDefaultModal from '../hooks/useDefaultModal';
import DefaultModal from './DefaultModal';

function EntidadeModal({ show, onClose, onSuccess, entidade }) {
  const [formData, setFormData] = useState({
    cnpj: '',
    nome: '',
    nomeFantasia: '',
    email: '',
    telefone: '',
    fax: '',
    logrNome: '',
    logrNum: '',
    logrCompl: '',
    logrBairro: '',
    logrCep: '',
    logrCidade: '',
    logrUf: '',
  });
  const [ufs, setUfs] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isBuscandoCnpj, setIsBuscandoCnpj] = useState(false);
  const token = localStorage.getItem('token');
  const refs = useRef({});
  const { setDefaultModal, showDefaultModal } = useDefaultModal();

  useEffect(() => {
    if (!show) return;
    if (entidade) {
      setFormData(prev => ({
        ...prev,
        ...entidade,
        nomeFantasia: entidade.nomeFantasia || '',
      }));
    } else {
      setFormData({
        cnpj: '',
        nome: '',
        nomeFantasia: '',
        email: '',
        telefone: '',
        fax: '',
        logrNome: '',
        logrNum: '',
        logrCompl: '',
        logrBairro: '',
        logrCep: '',
        logrCidade: '',
        logrUf: '',
      });
    }
    setValidated(false);
  }, [entidade, show]);

  useEffect(() => {
    if (!show) return;
    axios
      .get(`${API_BASE_URL.UF}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => setUfs(result.data))
      .catch(error => console.error('Erro ao obter listas:', error));
  }, [show, token]);

  useEffect(() => {
    if (!formData.logrUf) {
      setCidades([]);
      return;
    }
    axios
      .get(`${API_BASE_URL.MUNICIPIO}/uf/${formData.logrUf}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => setCidades(result.data))
      .catch(error => console.error('Erro ao carregar cidades:', error));
  }, [formData.logrUf, token]);

  const cidadeSelecionada = formData.logrCidade;
  const cidadeExiste = cidades.some(c => normalizeString(c.nome || '') === normalizeString(cidadeSelecionada));
  const cidadesComExtra = cidadeExiste || !cidadeSelecionada
    ? cidades
    : [...cidades, { nome: cidadeSelecionada }];

  function stripMask(value) {
    return value ? value.replace(/\D/g, '') : '';
  }

  function handleChange(event) {
    const { name, value, selectionStart } = event.target;

    if (name === 'telefone') {
      setFormData(prev => ({ ...prev, telefone: unformatTelefone(value) }));
      return;
    }

    if (name === 'celular') {
      setFormData(prev => ({ ...prev, celular: unformatCelular(value) }));
      return;
    }

    if (name === 'logrCep') {
      setFormData(prev => ({ ...prev, logrCep: unformatCEP(value) }));
      return;
    }

    if (name === 'logrUf') {
      setFormData(prev => ({ ...prev, logrUf: value, logrCidade: '' }));
      return;
    }

    if (name === 'cnpj') {
      const unformattedValue = unformatCNPJ(value);
      const formattedValue = formatCNPJ(unformattedValue);
      setFormData(prev => ({ ...prev, cnpj: formattedValue }));
      if (refs.current.cnpj) {
        const newPos = selectionStart + (formattedValue.length - unformattedValue.length);
        refs.current.cnpj.setSelectionRange(newPos, newPos);
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: name === 'email' ? value.toLowerCase() : value.toUpperCase(),
    }));
  }

  async function handleBuscarCnpj() {
    const cnpjNum = stripMask(formData.cnpj);

    if (cnpjNum.length !== 14) {
      showDefaultModal('error', 'Digite um CNPJ válido (14 dígitos) antes de buscar.');
      return;
    }

    setIsBuscandoCnpj(true);
    try {
      const response = await axios.get(`${API_BASE_URL.CNPJ_PROXY}/${cnpjNum}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;

      if (data.status === 'ERROR') {
        showDefaultModal('error', data.message || 'CNPJ não encontrado.');
        return;
      }

      const cidadeReceita = data.municipio || '';
      const ufReceita = data.uf || '';

      let cidadesParaMatch = cidades;
      if (ufReceita && ufReceita !== formData.logrUf) {
        try {
          const resCidades = await axios.get(`${API_BASE_URL.MUNICIPIO}/uf/${ufReceita}`, { headers: { Authorization: `Bearer ${token}` } });
          cidadesParaMatch = resCidades.data || [];
          setCidades(cidadesParaMatch);
        } catch (_) { cidadesParaMatch = []; }
      }

      const cidadeBanco = cidadesParaMatch.find(
        cidade => cidade.nome && cidadeReceita && normalizeString(cidade.nome) === normalizeString(cidadeReceita)
      );

      setFormData(prev => ({
        ...prev,
        nome: data.nome || prev.nome,
        nomeFantasia: data.fantasia || prev.nomeFantasia,
        email: data.email || prev.email,
        telefone: data.telefone || prev.telefone,
        celular: data.celular || prev.celular,
        logrNome: data.logradouro || prev.logrNome,
        logrNum: data.numero || prev.logrNum,
        logrCompl: data.complemento || prev.logrCompl,
        logrBairro: data.bairro || prev.logrBairro,
        logrCep: (data.cep || '').replace(/\D/g, '') || prev.logrCep,
        logrUf: ufReceita || prev.logrUf,
        logrCidade: cidadeBanco ? cidadeBanco.nome : prev.logrCidade,
      }));

      showDefaultModal('success', 'Dados carregados com sucesso!');
    } catch (error) {
      const status = error?.response?.status;
      if (status === 429) {
        showDefaultModal('error', 'Limite de consultas atingido. Aguarde 1 minuto e tente novamente.');
      } else if (status === 400) {
        showDefaultModal('error', 'CNPJ inválido ou não encontrado.');
      } else {
        showDefaultModal('error', `Erro ao consultar CNPJ${status ? ` (${status})` : ''}. Tente novamente em instantes.`);
      }
    } finally {
      setIsBuscandoCnpj(false);
    }
  }

  function handleClose() {
    if (onClose) onClose();
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!formData.cnpj || !formData.nome) {
      setValidated(true);
      return;
    }

    const unformattedEntidade = {
      ...formData,
      cnpj: unformatCNPJ(formData.cnpj),
      telefone: formData.telefone ? unformatTelefone(formData.telefone) : '',
      celular: formData.celular ? unformatCelular(formData.celular) : '',
      logrCep: formData.logrCep ? unformatCEP(formData.logrCep) : '',
    };

    if (!isValidCNPJ(unformattedEntidade.cnpj)) {
      showDefaultModal('error', 'CNPJ inválido');
      return;
    }

    setLoading(true);
    try {
      if (entidade) {
        await axios.put(`${API_BASE_URL.ENTIDADE}/update`, unformattedEntidade, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (onSuccess) onSuccess();
        handleClose();
        showDefaultModal('success', 'Registro atualizado com sucesso!');
      } else {
        await axios.post(`${API_BASE_URL.ENTIDADE}/save`, unformattedEntidade, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (onSuccess) onSuccess();
        handleClose();
        showDefaultModal('success', 'Registro salvo com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao salvar entidade:', error);
      showDefaultModal('error', 'Erro ao salvar o registro!');
    } finally {
      setLoading(false);
    }
  }

  if (!show) return null;

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: '90vw', height: '90vh' }}
        >
          <div className="modal-content" style={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '0.5rem', overflow: 'hidden' }}>
            <div
              className="modal-header text-white"
              style={{
                background:
                  'linear-gradient(45deg, rgb(19, 72, 156) 0%, rgba(19, 72, 156, 1) 100%)',
                padding: '0.35rem 0.75rem',
                border: 'none',
                flexShrink: 0,
              }}
            >
              <div className="d-flex align-items-center justify-content-between" style={{ width: '100%' }}>
                <h6 className="modal-title mb-0" style={{ fontSize: '0.80rem', lineHeight: 1 }}>
                  <i className="bi bi-building-add me-2"></i>
                  {entidade ? 'Editar Entidade' : 'Cadastro de Entidade'}
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-link text-white p-0 mb-0"
                    onClick={handleClose}
                    title="Fechar"
                    style={{ fontSize: '1rem', textDecoration: 'none' }}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-body p-3" style={{ flex: 1, overflowY: 'auto' }}>
              <form onSubmit={handleSubmit} noValidate>
                <div className="row g-3">
                  <div className="col-12 col-sm-3 col-md-3 input-group-sm">
                    <label className="form-label-sm">CNPJ</label>
                    <div className="input-group input-group-sm">
                      <CNPJMaskInput
                        value={formatCNPJ(formData.cnpj)}
                        onChange={e =>
                          handleChange({
                            target: {
                              name: 'cnpj',
                              value: e.target.value,
                            },
                          })
                        }
                        name="cnpj"
                        ref={el => (refs.current.cnpj = el)}
                        type="text"
                        readOnly={!!entidade}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleBuscarCnpj}
                        disabled={loading || isBuscandoCnpj || !formData.cnpj || stripMask(formData.cnpj).length !== 14 || !!entidade}
                        title="Buscar dados na Receita Federal"
                      >
                        {isBuscandoCnpj ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className="bi bi-search"></i>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="col-12 col-sm-5 col-md-5 input-group-sm">
                    <label className="form-label-sm">Nome</label>
                    <input
                      ref={el => (refs.current.nome = el)}
                      onChange={handleChange}
                      value={formData.nome}
                      name="nome"
                      type="text"
                      className={`form-control${validated && !formData.nome ? ' is-invalid' : ''}`}
                    />
                  </div>
                  <div className="col-12 col-sm-4 col-md-4 input-group-sm">
                    <label className="form-label-sm">Nome Fantasia</label>
                    <input
                      ref={el => (refs.current.nomeFantasia = el)}
                      onChange={handleChange}
                      value={formData.nomeFantasia || ''}
                      name="nomeFantasia"
                      type="text"
                      className="form-control"
                    />
                  </div>
                  <div className="col-md-6 input-group-sm">
                    <label className="form-label-sm">Email</label>
                    <input
                      ref={el => (refs.current.email = el)}
                      onChange={handleChange}
                      value={formData.email}
                      name="email"
                      type="text"
                      className="form-control"
                    />
                  </div>
                  <div className="col-md-3 input-group-sm">
                    <label className="form-label-sm">Telefone</label>
                    <TelefoneMaskInput
                      value={formatTelefone(formData.telefone)}
                      onChange={e =>
                        handleChange({
                          target: { name: 'telefone', value: e.target.value },
                        })
                      }
                      name="telefone"
                      ref={el => (refs.current.telefone = el)}
                    />
                  </div>
                  <div className="col-md-3 input-group-sm">
                    <label className="form-label-sm">Celular</label>
                    <CelularMaskInput
                      value={formatCelular(formData.celular)}
                      onChange={e =>
                        handleChange({
                          target: { name: 'celular', value: e.target.value },
                        })
                      }
                      name="celular"
                      ref={el => (refs.current.celular = el)}
                    />
                  </div>
                  <div className="col-md-6 input-group-sm">
                    <label className="form-label-sm">Endereço</label>
                    <input
                      ref={el => (refs.current.logrNome = el)}
                      onChange={handleChange}
                      value={formData.logrNome}
                      name="logrNome"
                      type="text"
                      className="form-control"
                    />
                  </div>
                  <div className="col-md-2 input-group-sm">
                    <label className="form-label-sm">Número</label>
                    <input
                      ref={el => (refs.current.logrNum = el)}
                      onChange={handleChange}
                      value={formData.logrNum}
                      name="logrNum"
                      type="text"
                      className="form-control"
                    />
                  </div>
                  <div className="col-md-4 input-group-sm">
                    <label className="form-label-sm">Complemento</label>
                    <input
                      ref={el => (refs.current.logrCompl = el)}
                      onChange={handleChange}
                      value={formData.logrCompl}
                      name="logrCompl"
                      type="text"
                      className="form-control"
                    />
                  </div>
                  <div className="col-md-4 input-group-sm">
                    <label className="form-label-sm">Bairro</label>
                    <input
                      ref={el => (refs.current.logrBairro = el)}
                      onChange={handleChange}
                      value={formData.logrBairro}
                      name="logrBairro"
                      type="text"
                      className="form-control"
                    />
                  </div>
                  <div className="col-md-3 input-group-sm">
                    <label className="form-label-sm">CEP</label>
                    <CEPMaskInput
                      value={formatCEP(formData.logrCep)}
                      onChange={e =>
                        handleChange({
                          target: { name: 'logrCep', value: e.target.value },
                        })
                      }
                      name="logrCep"
                      ref={el => (refs.current.cep = el)}
                    />
                  </div>
                  <div className="col-md-2 input-group-sm">
                    <label className="form-label-sm">UF</label>
                    <select
                      className="form-select"
                      value={formData.logrUf}
                      onChange={handleChange}
                      name="logrUf"
                      disabled={loading}
                    >
                      <option value="">Selecione uma UF...</option>
                      {ufs.map((uf, index) => (
                        <option key={index} value={uf.sigla}>
                          {uf.sigla}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3 input-group-sm">
                    <label className="form-label-sm">Cidade</label>
                    <select
                      className="form-select"
                      value={formData.logrCidade}
                      onChange={handleChange}
                      name="logrCidade"
                      disabled={loading || !formData.logrUf}
                    >
                      <option value="">Selecione uma cidade...</option>
                      {cidadesComExtra.map((cidade, index) => (
                        <option key={index} value={cidade.nome}>
                          {cidade.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleClose}
                disabled={loading}
              >
                <i className="bi bi-x-circle me-1"></i>
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-1" /> Salvar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <DefaultModal {...setDefaultModal} />
    </>
  );
}

export default EntidadeModal;
